/**
 * Almari · upgrade card.
 *
 * 4-tier paywall surfaced as an editorial subscription card.
 *   Free                    → core ledger + tier card + capsule grid
 *   Pro     ₹999/yr         → outfit recommender + CLIP vault + carbon + wishlist + Razorpay
 *   Family  ₹2999/yr        → 4 members + shared circle + couples view
 *   Studio  ₹5K setup + ₹999/mo per client → boutique stylist console (v1.1)
 *
 * Stores the active tier locally for v0 (no server verification yet).
 */
import { Payment, Storage } from '../chassis/index.ts';

const TIER_FILE = 'almari-tier.json';

interface TierState {
  readonly tier: Payment.TierId;
  readonly purchased_at: string | null;
  readonly payment_id: string | null;
}

async function loadTier(): Promise<TierState> {
  return (await Storage.readFile<TierState>(TIER_FILE)) ?? { tier: 'free', purchased_at: null, payment_id: null };
}
async function saveTier(t: TierState): Promise<void> {
  await Storage.writeFile(TIER_FILE, t);
}

export class AlmariUpgradeCard extends HTMLElement {
  #shadow = this.attachShadow({ mode: 'open' });
  #state: TierState = { tier: 'free', purchased_at: null, payment_id: null };

  async connectedCallback(): Promise<void> {
    this.#state = await loadTier();
    this.#render();
  }

  async #checkout(tier: Exclude<Payment.TierId, 'free'>): Promise<void> {
    try {
      const res = await Payment.checkout(tier);
      if (!res.dismissed && res.paymentId) {
        const next: TierState = { tier, purchased_at: new Date().toISOString(), payment_id: res.paymentId };
        await saveTier(next);
        this.#state = next;
        this.#render();
      }
    } catch (e) {
      console.warn('checkout failed', e);
    }
  }

  #render(): void {
    const currentTier = this.#state.tier;
    const isPro = currentTier === 'pro' || currentTier === 'family' || currentTier === 'studio_b2b';

    this.#shadow.innerHTML = `
<style>
:host { display: block; }
.head { padding-left: 4px; margin-bottom: 12px; display: grid; gap: 4px; }
.eyebrow { font-family: var(--mono); font-size: 9px; letter-spacing: 0.42em; text-transform: uppercase; color: var(--brass-dim); }
.title { font-family: var(--display); font-style: italic; font-size: 22px; color: var(--ink); font-weight: 600; letter-spacing: -0.005em; }
.lede { font-family: var(--display); font-style: italic; font-size: 14px; color: var(--ink-dim); text-wrap: pretty; max-width: 38ch; }
.grid { display: grid; gap: 10px; grid-template-columns: 1fr; }
@container (min-width: 460px) { .grid { grid-template-columns: 1fr 1fr; } }
.tier {
  background: var(--paper-card); border: 1px solid var(--rule); border-radius: 8px;
  padding: 18px; isolation: isolate; display: grid; gap: 8px; position: relative; overflow: hidden;
}
.tier::before {
  content: ''; position: absolute; inset: 6px; border-radius: 4px; pointer-events: none;
  border: 1px solid color-mix(in oklch, var(--brass) 14%, transparent);
}
.tier-name { font-family: var(--display); font-style: italic; font-size: 18px; color: var(--ink); font-weight: 700; letter-spacing: -0.01em; }
.tier-price { font-family: var(--display); font-size: 28px; font-weight: 700; color: var(--ink-deep); letter-spacing: -0.02em; line-height: 1; font-feature-settings: 'tnum' 1; }
.tier-period { font-family: var(--mono); font-size: 10px; letter-spacing: 0.32em; color: var(--ink-faint); text-transform: uppercase; }
.tier ul { font-family: var(--display); font-size: 14px; color: var(--ink-dim); list-style: none; padding: 0; margin: 6px 0 12px; display: grid; gap: 4px; }
.tier ul li::before { content: '·'; margin-right: 6px; color: var(--brass-deep); font-weight: 700; }
.cta {
  font-family: var(--mono); font-size: 10px; letter-spacing: 0.36em; text-transform: uppercase;
  padding: 12px 16px; background: var(--ink-deep); color: var(--paper); border-radius: 6px;
  font-weight: 700; cursor: pointer; border: 0; transition: filter 140ms;
}
.cta:hover { filter: brightness(1.1); }
.cta[disabled] { opacity: 0.5; cursor: default; }
.tier.is-current { border-color: oklch(50% 0.13 145); border-left: 3px solid oklch(50% 0.13 145); }
.tier.is-current .cta { background: oklch(50% 0.13 145); }
.refund { font-family: var(--mono); font-size: 9px; letter-spacing: 0.22em; text-transform: uppercase; color: var(--ink-faint); margin-top: 14px; padding-top: 12px; border-top: 1px dashed var(--rule); text-align: center; }
</style>
<header class="head">
  <span class="eyebrow">Membership · empire imprint</span>
  <h3 class="title">${isPro ? 'Pro tier locked · shukriya' : 'Almari Pro pe upgrade karein.'}</h3>
  <p class="lede">Almari Pro unlocks the CLIP vault, weather-aware outfit recommender, wardrobe carbon, brand-URL wishlist + family sharing. Setup ek baar.</p>
</header>
<div class="grid">
  <div class="tier ${currentTier === 'pro' ? 'is-current' : ''}">
    <div class="tier-name">Pro</div>
    <div class="tier-price">₹999<span class="tier-period"> / yr</span></div>
    <ul>
      <li>CLIP vault — snap, embed, search</li>
      <li>Outfit recommender — weather + ROI</li>
      <li>Wardrobe carbon — LCA breakdown</li>
      <li>Wishlist — brand-URL paste</li>
    </ul>
    <button class="cta" data-tier="pro" ${currentTier === 'pro' ? 'disabled' : ''}>${currentTier === 'pro' ? 'Active' : 'Get Pro'}</button>
  </div>
  <div class="tier ${currentTier === 'family' ? 'is-current' : ''}">
    <div class="tier-name">Family</div>
    <div class="tier-price">₹2,999<span class="tier-period"> / yr</span></div>
    <ul>
      <li>4 members · shared closet + couples view</li>
      <li>Pro features for everyone</li>
      <li>Mom / wife / Naina inclusive</li>
      <li>Single Razorpay autopay</li>
    </ul>
    <button class="cta" data-tier="family" ${currentTier === 'family' ? 'disabled' : ''}>${currentTier === 'family' ? 'Active' : 'Get Family'}</button>
  </div>
</div>
<div class="refund">30-day money-back · DPDP-compliant · cancel anytime via aditya@taxwalaai.com</div>
`;

    this.#shadow.querySelectorAll<HTMLButtonElement>('.cta').forEach((b) => {
      b.addEventListener('click', () => {
        const t = b.dataset.tier as Exclude<Payment.TierId, 'free'>;
        if (t && !b.hasAttribute('disabled')) void this.#checkout(t);
      });
    });
  }
}

customElements.define('almari-upgrade-card', AlmariUpgradeCard);
